import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle, XCircle, AlertCircle, FileText, Calendar, User } from 'lucide-react';
import { Invoice, User as UserType } from '@/data/mockData';
import { formatCurrency } from '@/lib/utils';

interface InvoiceVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  invoice: Invoice | null;
  onVerify: (invoiceId: number, verified: boolean, notes?: string) => void;
  currentUser: UserType | null;
}

export const InvoiceVerificationModal: React.FC<InvoiceVerificationModalProps> = ({
  isOpen,
  onClose,
  invoice,
  onVerify,
  currentUser
}) => {
  const [verificationNotes, setVerificationNotes] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);

  const handleVerify = async (verified: boolean) => {
    if (!invoice) return;

    setIsVerifying(true);
    try {
      await onVerify(invoice.id, verified, verificationNotes);
      setVerificationNotes('');
      onClose();
    } catch (error) {
      console.error('Error verifying invoice:', error);
    } finally {
      setIsVerifying(false);
    }
  };


  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (!invoice) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Invoice Verification - {invoice.invoiceNumber}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Invoice Details */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Invoice Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Invoice Number</Label>
                  <p className="text-sm font-medium">{invoice.invoiceNumber}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Invoice Date</Label>
                  <p className="text-sm font-medium">{formatDate(invoice.invoiceDate)}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Total Amount</Label>
                  <p className="text-sm font-medium">{formatCurrency(invoice.totalAmount)}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Status</Label>
                  <Badge variant={invoice.status === 'Paid' ? 'default' : 'secondary'}>
                    {invoice.status}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* PO Details */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Related Purchase Order</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label className="text-sm font-medium text-muted-foreground">PO Number:</Label>
                  <span className="text-sm font-medium">PO-{invoice.poId}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-sm font-medium text-muted-foreground">Vendor:</Label>
                  <span className="text-sm">{invoice.vendorName}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Invoice Items */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Invoice Items</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {invoice.items.map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{item.skuName}</span>
                        <span className="text-sm text-muted-foreground">({item.skuCode})</span>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Qty: {item.quantity} × {formatCurrency(item.unitPrice)} = {formatCurrency(item.totalPrice)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Security Verification Section */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <AlertCircle className="h-5 w-5" />
                Security Verification
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-yellow-800">Verification Required</h4>
                    <p className="text-sm text-yellow-700 mt-1">
                      Please verify the invoice details against the received goods and documentation before marking as verified.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <Label htmlFor="verification-notes" className="text-sm font-medium">
                  Verification Notes
                </Label>
                <Textarea
                  id="verification-notes"
                  placeholder="Add any notes about the verification process..."
                  value={verificationNotes}
                  onChange={(e) => setVerificationNotes(e.target.value)}
                  className="mt-1"
                  rows={3}
                />
              </div>

              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <User className="h-4 w-4" />
                <span>Verifying as: {currentUser?.name || 'Unknown User'}</span>
                <Calendar className="h-4 w-4 ml-2" />
                <span>{new Date().toLocaleDateString('en-IN')}</span>
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={isVerifying}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => handleVerify(false)}
              disabled={isVerifying}
              className="flex items-center gap-2"
            >
              <XCircle className="h-4 w-4" />
              Reject Invoice
            </Button>
            <Button
              onClick={() => handleVerify(true)}
              disabled={isVerifying}
              className="flex items-center gap-2"
            >
              <CheckCircle className="h-4 w-4" />
              {isVerifying ? 'Verifying...' : 'Verify Invoice'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

